import { ReactNode } from "react";
import { formatAssetLabel } from "./marketUtils";
import type { Market } from "./wsTypes";

/**
 * Turns token names in a headline into links to that token's numeraire
 * market. Each competition token is known by its ticker (from the market's
 * base-asset label) and its in-fiction company name(s); we match either.
 *
 * The company aliases below are for the current flavor set — extend them if
 * the competition ships different tokens/brands.
 */
const COMPANY_ALIASES: Record<string, string[]> = {
  VAN: ["Vanilla"],
  CHOC: ["ChocoWorks", "Chocolate"],
  MINT: ["Mint Condition", "Mint"],
  FUDGE: ["Fudge Factory", "Fudge"],
  SWIRL: ["Swirl"],
  SCOOP: ["Scoop"],
};

const NUMERAIRE_LABEL = "VAN";

export interface TokenLinkIndex {
  regex: RegExp | null;
  /** lowercased alias -> market key */
  lookup: Map<string, string>;
}

/** Build a matcher over every token that has a `<token>/VAN` market. */
export function buildTokenLinkIndex(markets: readonly Market[]): TokenLinkIndex {
  const lookup = new Map<string, string>();
  const aliases: string[] = [];
  for (const m of markets) {
    if (formatAssetLabel(m.tradingPair.quote) !== NUMERAIRE_LABEL) continue;
    const symbol = formatAssetLabel(m.tradingPair.base);
    for (const name of [symbol, ...(COMPANY_ALIASES[symbol] ?? [])]) {
      if (!lookup.has(name.toLowerCase())) {
        lookup.set(name.toLowerCase(), m.key);
        aliases.push(name);
      }
    }
  }
  if (aliases.length === 0) return { regex: null, lookup };
  // Longest first so multi-word brands ("Mint Condition") win over "Mint".
  aliases.sort((a, b) => b.length - a.length);
  const escaped = aliases.map((a) => a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return { regex: new RegExp(`\\b(${escaped.join("|")})\\b`, "gi"), lookup };
}

/**
 * Render a headline with each recognized token name as a trade link.
 * `onTrade(marketKey)` navigates to that market.
 */
export function linkifyHeadline(
  headline: string,
  index: TokenLinkIndex,
  onTrade: (marketKey: string) => void,
): ReactNode {
  if (!index.regex) return headline;
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  index.regex.lastIndex = 0;
  while ((match = index.regex.exec(headline)) !== null) {
    const [text] = match;
    const start = match.index;
    if (start > last) parts.push(headline.slice(last, start));
    const marketKey = index.lookup.get(text.toLowerCase());
    if (marketKey) {
      parts.push(
        <button
          key={start}
          onClick={(e) => {
            e.stopPropagation();
            onTrade(marketKey);
          }}
          className="sr-focus-ring align-baseline font-medium text-sr-lemon-muted underline decoration-sr-lemon-muted/40 decoration-dotted underline-offset-2 transition-colors hover:decoration-solid"
        >
          {text}
        </button>,
      );
    } else {
      parts.push(text);
    }
    last = start + text.length;
    if (match.index === index.regex.lastIndex) index.regex.lastIndex++; // guard against zero-width
  }
  if (last < headline.length) parts.push(headline.slice(last));
  return parts;
}
